import React, {useEffect, useMemo, useRef, useState} from "react";
import {FlowProps} from "../../types/FlowProps";
import {MessageTemplateProps} from "../../types/MessageTemplateProps";
import LongArrowRight from "../../assets/icons/LongArrowRight";
import WriteIcon from "../../assets/icons/WriteIcon";
import PresetsIcon from "../../assets/icons/PresetsIcon";
import {getFriendlyTimeDifference, millisecondsToHuman} from "../../utils/friendlyDate";
import {useNavigate} from "react-router-dom";
import StatusDot from "../../components/statusDot/StatusDot";
import EmptyState from "../../components/emptyState/EmptyState";
import {EmptyIllustration} from "../../assets/illustrations/EmptyIllustration";
import useAppContext from "../../hooks/useAppContext";
import {FlowTriggerProps} from "../../types/FlowTriggerProps";

interface FlowRecordProps {
    index: number;
    flowTypeId: number;
    flow: FlowProps;
    onNotificationClick: (template: MessageTemplateProps) => () => void;
    setIsNewNotificationModalOpen: (newState: boolean) => void;
    setIsPresetsModalOpen: (newState: boolean) => void;
    stats: any;
}

const FlowRecord = ({ index, flowTypeId, flow, onNotificationClick, setIsNewNotificationModalOpen, setIsPresetsModalOpen, stats }: FlowRecordProps) => {
    const templatesListContainerRef = useRef<HTMLDivElement | null>(null);
    const templatesListRef = useRef<HTMLDivElement | null>(null);
    const currentTemplatesListX = useRef(0);
    const maxLeft = 0;
    const minLeft = useMemo(() => {
        if(!templatesListRef.current || !templatesListContainerRef.current) return 0;
        const containerRect = templatesListContainerRef.current?.getBoundingClientRect();
        const rect = templatesListRef.current?.getBoundingClientRect();
        return 0 - rect.width + containerRect.width;
    }, [templatesListContainerRef.current, templatesListRef.current]);
    const navigate = useNavigate();
    const { data: { flowTriggers } } = useAppContext();

    const [isMaxRight, setIsMaxRight] = useState(true);
    const [isMaxLeft, setIsMaxLeft] = useState(true);
    const [maxOffset, setMaxOffset] = useState(0);

    useEffect(() => {
        navigationHandler();
        const container = templatesListContainerRef.current?.getBoundingClientRect();
        const inner = templatesListRef.current?.getBoundingClientRect();
        if(container && inner) {
            setMaxOffset(inner.width - container.width);
        }
    }, [minLeft, maxLeft]);

    const navigationHandler = () => {
        const nextIsMaxRight = currentTemplatesListX.current <= minLeft;
        const nextIsMaxLeft = currentTemplatesListX.current >= maxLeft;
        if(nextIsMaxRight !== isMaxRight) setIsMaxRight(nextIsMaxRight);
        if(nextIsMaxLeft !== isMaxLeft) setIsMaxLeft(nextIsMaxLeft);
    }

    const moveRight = () => {
        if(!templatesListRef.current || !templatesListContainerRef.current) return;
        templatesListRef.current?.classList.add('with-animation');
        const currentX = currentTemplatesListX.current;
        const nextX = Math.max(minLeft, currentX - 288);
        currentTemplatesListX.current = nextX;
        templatesListRef.current.style.left = `${nextX}px`;
        navigationHandler();
    }

    const moveLeft = () => {
        if(!templatesListRef.current || !templatesListContainerRef.current) return;
        templatesListRef.current?.classList.add('with-animation');
        const currentX = currentTemplatesListX.current;
        const nextX = Math.min(maxLeft, currentX + 288);
        currentTemplatesListX.current = nextX;
        templatesListRef.current.style.left = `${nextX}px`;
        navigationHandler();
    }

    const onWheel = (event: WheelEvent) => {
        if(!templatesListRef.current || !templatesListContainerRef.current || templatesListContainerRef.current?.clientWidth > templatesListRef.current?.clientWidth) return;
        if(!event.deltaX || !templatesListRef.current) return;
        const currentX = currentTemplatesListX.current;
        const nextX = Math.max(Math.min(currentX + event.deltaX, maxLeft), -maxOffset);
        currentTemplatesListX.current = nextX;
        templatesListRef.current.style.left = `${nextX}px`;
        navigationHandler();
    }

    const onTransitionEnd = () => {
        templatesListRef.current?.classList.remove('with-animation');
    }

    const newTemplateButtonOptions = {
        items: [{
            key: '1',
            className: 'dropdown-item',
            label: (
                <div onClick={() => setIsNewNotificationModalOpen(true)}>
                    <WriteIcon width={14} height={14} color='rgba(0,0,0,1)' /><span>Create from Scratch</span>
                </div>
            ),
        }, {
            key: '2',
            className: 'dropdown-item',
            label: (
                <div onClick={() => setIsPresetsModalOpen(true)}>
                    <PresetsIcon width={14} height={14} color='rgba(0,0,0,1)' /><span>Build with Wizard</span>
                </div>
            ),
        },]
    };

    const onFlowClick = (flowId: number) => () => {
        navigate(`/automation/${flowId}`);
    }

    useEffect(() => {
        templatesListRef.current?.addEventListener("wheel", onWheel);
        templatesListRef.current?.addEventListener("transitionend", onTransitionEnd);
        return () => {
            templatesListRef.current?.removeEventListener("wheel", onWheel);
            templatesListRef.current?.removeEventListener("transitionend", onTransitionEnd);
        }
    }, [onWheel, maxOffset]);

    const activeTemplates = flow.templates?.filter(template => template.active);
    let zIndex = 1;

    return (
        <div className={`flow-container ${flow.active ? 'active' : 'disabled'}`} key={`flow-type-${flowTypeId}-flow-${flow.id}`} onClick={onFlowClick(flow.id)}>
            <div className="flow-inner">
                <div className="title"><StatusDot status={flow.active ? 'enabled' : 'disabled'} />{flow.title}</div>
                <div className="flow-preview">
                    <div className={`template-preview trigger trigger-${flow.flowTriggerId}`}><div className="title">
                        {flowTriggers.data?.find((flowTrigger: FlowTriggerProps) => flowTrigger.id === flow.flowTriggerId)?.title}
                    </div></div>
                    <div className={`arrow-container trigger-${flow.flowTriggerId}`}><LongArrowRight width={48} height={48} color="#3e4a8a" /></div>
                    {activeTemplates?.length ? activeTemplates?.map((template, index) => {
                        const triggerDelayStr = template.triggerDelay === 0 ? 'No ' : millisecondsToHuman(template.triggerDelay*1000);
                        const triggerDelayArr = triggerDelayStr?.split(' ') || [];
                        zIndex = zIndex + 1;
                        return <div key={`flow-${flow.id}-template-${template.id}`} className="template-delay-container" style={{ zIndex: zIndex, transitionDelay: `${0.1*(index + 1)}s` }}>
                            <div className="trigger-delay"><span>{triggerDelayArr[0]}</span>{triggerDelayArr[1]} <div>delay</div></div>
                            <div className="template-preview"><div className="title">{template.title}</div>
                                {['PENDING','REJECTED'].includes(template.whatsappTemplateStatus as any) ? <div className={`status-indicator ${template.whatsappTemplateStatus}`}>
                                    <StatusDot status={
                                        template.whatsappTemplateStatus === 'PENDING' ? 'pending' :
                                            template.whatsappTemplateStatus === 'REJECTED' ? 'rejected' : 'enabled'
                                    } />
                                    {/*<span>{template.whatsappTemplateStatus?.toLowerCase()}</span>*/}
                                </div> : null}
                            </div>
                            {/*{index < (activeTemplates?.length || 0) - 1 ? <div className="arrow-container"><LongArrowRight width={48} height={48} color="#eeeeee" /></div> : null}*/}
                        </div>;
                    }) : <div className="no-templates-view">
                            <div>This automation has no templates yet. Add templates to define its messages and timing.</div>
                        </div>
                    }
                </div>
                <div className="flow-stats">
                    <div className="row-title">
                        Last 7 Days Performance:
                    </div>
                    <div className="stats-item">
                        <div className="stats-value">{parseInt(stats?.total_checkouts || 0).toLocaleString()}</div>
                        <div className="stats-title">Times<br />Triggered</div>
                    </div>
                    <div className="stats-item">
                        <div className="stats-value">{parseInt(stats?.delivered_messages || 0).toLocaleString()}</div>
                        <div className="stats-title">Messages<br />Delivered</div>
                    </div>
                    <div className="stats-item">
                        <div className="stats-value">{parseInt(stats?.total_orders || 0).toLocaleString()}</div>
                        <div className="stats-title">Successful<br />Recoveries</div>
                    </div>
                    <div className="stats-item">
                        <div className="stats-value">${parseInt(stats?.total_revenue || 0).toLocaleString()}</div>
                        <div className="stats-title">Recovered<br />Revenue</div>
                    </div>
                    <div className="stats-item">
                        <div className="stats-value">${parseInt(stats?.revenue_per_send || 0).toLocaleString()}</div>
                        <div className="stats-title">Revenue<br />Per Send</div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default FlowRecord;